/**
 * /main exec layout (quick-261104-ia3, John 2026-11-04): the always-visible top
 * band (ARR → churn → pipeline → monitors) and the department tab panels.
 * Every row is a 12-column grid using only spans 4 / 5 / 6 / 7 / 8 / 12, and
 * tiles in a row share one height, so the page lines up.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { SALES_EFFECTIVENESS_URL } from '../../lib/exec/links.js';
import { computeChanges, computeProblems, type ProblemItem } from '../../lib/exec/monitors.js';
import { todayIso } from '../../lib/exec/periods.js';

import {
  ArrBridgeTile,
  ArrMovementTile,
  ChurnedCustomersTile,
  ConcentrationTile,
  FinanceLinksTile,
  RetentionTile,
  ServicesForecastLinkTile,
  ServicesPipelineTile,
  ServicesWonTile,
  SfRenewalsTile,
  UpcomingRenewalsTile,
} from './DeptTiles.js';
import { ArrTrendTile, ChurnTile, FunnelTile, FunnelTrendTile, PotentialOppsDialog } from './ExecTopTiles.js';
import { GroupedTile } from './GroupedTile.js';
import { ChangesTile, ProblemsTile } from './MonitorTiles.js';
import { RilletRefreshButton } from './RilletRefreshButton.js';
import {
  AvgDealTile,
  BigDealsTile,
  CoverageTile,
  CycleBySizeTile,
  CycleTrendTile,
  LossReasonTile,
  NextYearPipelineTile,
  OppsCreatedTile,
  OutboundTile,
  PeriodCountTile,
  SlippageTile,
  StalledTile,
  WinRateTile,
} from './SalesTiles.js';

import type { ExecResponse, MainScorecardResponse } from '@saledash/shared';

const SPAN: Record<number, string> = {
  4: 'col-span-12 lg:col-span-4',
  5: 'col-span-12 lg:col-span-5',
  6: 'col-span-12 lg:col-span-6',
  7: 'col-span-12 lg:col-span-7',
  8: 'col-span-12 lg:col-span-8',
  12: 'col-span-12',
};

export function Row({ children }: { children: ReactNode }): React.JSX.Element {
  return <div className="grid grid-cols-12 gap-3">{children}</div>;
}
export function Cell({ span, children }: { span: 4 | 5 | 6 | 7 | 8 | 12; children: ReactNode }): React.JSX.Element {
  return <div className={`${SPAN[span] ?? 'col-span-12'} min-w-0`}>{children}</div>;
}
export function SectionHead({ label, right }: { label: string; right?: ReactNode }): React.JSX.Element {
  return (
    <div className="mb-2 mt-1 flex items-center gap-3">
      <h2 className="section-label shrink-0">{label}</h2>
      <span aria-hidden className="h-px flex-1 bg-gradient-to-r from-ink-200 to-transparent" />
      {right}
    </div>
  );
}

export type ExecTab = 'sales' | 'marketing' | 'cs' | 'services' | 'finance';

// ─── Top band ───────────────────────────────────────────────────────────────
export function ExecBand({
  exec,
  scorecard,
  loading,
  onOpenTab,
}: {
  exec: ExecResponse | undefined;
  scorecard: MainScorecardResponse | undefined;
  loading: boolean;
  onOpenTab: (t: ExecTab) => void;
}): React.JSX.Element {
  const today = todayIso();
  const funnel = exec?.panels.funnel?.payload;
  const avgDeal = exec?.avgDealSize ?? scorecard?.grouped['MAIN-12']?.total_value ?? null;
  const problems = useMemo(() => computeProblems(exec, scorecard, today), [exec, scorecard, today]);
  const changes = useMemo(() => computeChanges(exec), [exec]);
  const [auditOpen, setAuditOpen] = useState(false);
  const openAudit = useCallback(() => {
    setAuditOpen(true);
  }, []);
  const closeAudit = useCallback(() => {
    setAuditOpen(false);
  }, []);
  return (
    <div className="space-y-3" data-testid="exec-band">
      <SectionHead
        label="Revenue"
        right={
          <span className="flex flex-wrap items-center justify-end gap-2">
          <RilletRefreshButton />
          <a
            href={SALES_EFFECTIVENESS_URL}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 rounded-full border border-ink-200 bg-white px-3 py-1 text-xs font-medium text-brand-600 hover:border-brand-300"
          >
            Sales effectiveness by rep ↗
          </a>
          </span>
        }
      />
      <Row>
        <Cell span={7}>
          <ArrTrendTile />
        </Cell>
        <Cell span={5}>
          <ChurnTile />
        </Cell>
      </Row>
      <SectionHead label="Pipeline" />
      <Row>
        <Cell span={5}>
          <FunnelTile funnel={funnel} avgDeal={avgDeal} loading={loading} onAuditPotential={openAudit} />
        </Cell>
        <Cell span={7}>
          <FunnelTrendTile funnel={funnel} avgDeal={avgDeal} loading={loading} />
        </Cell>
      </Row>
      <SectionHead label="Monitor" />
      <Row>
        <Cell span={6}>
          <ProblemsTile
            items={problems}
            loading={loading}
            onOpenTab={(t: NonNullable<ProblemItem['tab']>) => {
              onOpenTab(t);
            }}
            onOpenPotential={openAudit}
          />
        </Cell>
        <Cell span={6}>
          <ChangesTile items={changes} loading={loading} />
        </Cell>
      </Row>
      <PotentialOppsDialog open={auditOpen} onClose={closeAudit} funnel={funnel} avgDeal={avgDeal} />
    </div>
  );
}

// ─── Department tabs ────────────────────────────────────────────────────────
export function SalesPanel({ exec, scorecard }: { exec: ExecResponse | undefined; scorecard: MainScorecardResponse | undefined }): React.JSX.Element {
  const act = exec?.panels.activity?.payload;
  return (
    <div className="space-y-3">
      <SectionHead label="Speed" />
      <Row>
        <Cell span={6}>
          <CycleTrendTile scorecard={scorecard} />
        </Cell>
        <Cell span={6}>
          <CycleBySizeTile cycle={exec?.panels.cycle?.payload} />
        </Cell>
      </Row>
      <SectionHead label="Activity" />
      <Row>
        <Cell span={8}>
          <OutboundTile activity={act} />
        </Cell>
        <Cell span={4}>
          <PeriodCountTile
            title="New meetings booked"
            info="New-business meetings (inbound and outbound) put on the calendar in each period. Switch between week, month and quarter."
            points={act?.meetingsBooked}
            since={act?.since}
            footer="Salesforce events, meeting type Inbound New / Outbound New"
            testId="sales-meetings"
            tipText="New-business meetings booked in this period."
          />
        </Cell>
      </Row>
      <Row>
        <Cell span={8}>
          <OppsCreatedTile activity={act} />
        </Cell>
        <Cell span={4}>
          <PeriodCountTile
            title="Opportunities advanced"
            info="How many times a deal moved forward to a later stage (or to won) in each period. More moves means deals are progressing, not sitting."
            points={act?.oppsAdvanced}
            since={act?.since}
            footer="Forward stage moves, product deals"
            testId="sales-advanced"
            tipText="Forward stage moves in this period."
            options={['week', 'month', 'quarter']}
          />
        </Cell>
      </Row>
      <SectionHead label="Pipeline" />
      <Row>
        <Cell span={5}>
          <CoverageTile scorecard={scorecard} />
        </Cell>
        <Cell span={7}>
          <NextYearPipelineTile coverage={exec?.panels.coverage?.payload} />
        </Cell>
      </Row>
      <Row>
        <Cell span={6}>
          <BigDealsTile scorecard={scorecard} />
        </Cell>
        <Cell span={6}>
          <AvgDealTile scorecard={scorecard} />
        </Cell>
      </Row>
      <SectionHead label="Efficiency" />
      <Row>
        <Cell span={6}>
          <WinRateTile winrate={exec?.panels.winrate?.payload} />
        </Cell>
        <Cell span={6}>
          <StalledTile scorecard={scorecard} />
        </Cell>
      </Row>
      <Row>
        <Cell span={6}>
          <SlippageTile scorecard={scorecard} />
        </Cell>
        <Cell span={6}>
          <LossReasonTile scorecard={scorecard} />
        </Cell>
      </Row>
    </div>
  );
}

/** Marketing — one tab, every series in the same frame (no collapsed sections). */
const MARKETING_ROWS: string[][] = [
  ['MAIN-27', 'MAIN-28'],
  ['MAIN-29', 'MAIN-31'],
  ['MAIN-30', 'MAIN-32'],
  ['MAIN-33', 'MAIN-49'],
  ['MAIN-34', 'MAIN-35'],
  ['MAIN-50', 'MAIN-51'],
  ['MAIN-36'],
];

export function MarketingPanel({ scorecard }: { scorecard: MainScorecardResponse | undefined }): React.JSX.Element {
  return (
    <div className="space-y-3">
      {MARKETING_ROWS.map((row) => (
        <Row key={row.join('-')}>
          {row.map((id) => (
            <Cell key={id} span={row.length === 1 ? 12 : 6}>
              <GroupedTile metricId={id} data={scorecard?.grouped[id]} />
            </Cell>
          ))}
        </Row>
      ))}
    </div>
  );
}

export function CsPanel({ exec }: { exec: ExecResponse | undefined }): React.JSX.Element {
  // Churn itself sits in the top band; this tab is retention, renewals and concentration.
  return (
    <div className="space-y-3">
      <SectionHead label="Customers" right={<RilletRefreshButton />} />
      <Row>
        <Cell span={6}>
          <RetentionTile />
        </Cell>
        <Cell span={6}>
          <SfRenewalsTile cs={exec?.panels.cs?.payload} />
        </Cell>
      </Row>
      <Row>
        <Cell span={4}>
          <UpcomingRenewalsTile />
        </Cell>
        <Cell span={4}>
          <ChurnedCustomersTile />
        </Cell>
        <Cell span={4}>
          <ConcentrationTile />
        </Cell>
      </Row>
    </div>
  );
}

export function ServicesPanel({ exec }: { exec: ExecResponse | undefined }): React.JSX.Element {
  const svc = exec?.panels.services?.payload;
  return (
    <div className="space-y-3">
      <Row>
        <Cell span={4}>
          <ServicesWonTile services={svc} />
        </Cell>
        <Cell span={4}>
          <ServicesPipelineTile services={svc} />
        </Cell>
        <Cell span={4}>
          <ServicesForecastLinkTile />
        </Cell>
      </Row>
    </div>
  );
}

export function FinancePanel({ scorecard }: { scorecard: MainScorecardResponse | undefined }): React.JSX.Element {
  return (
    <div className="space-y-3">
      <SectionHead label="Recurring revenue" right={<RilletRefreshButton />} />
      <Row>
        <Cell span={6}>
          <ArrBridgeTile />
        </Cell>
        <Cell span={6}>
          <ArrMovementTile />
        </Cell>
      </Row>
      <Row>
        <Cell span={6}>
          <GroupedTile metricId="MAIN-42" data={scorecard?.grouped['MAIN-42']} />
        </Cell>
        <Cell span={6}>
          <GroupedTile metricId="MAIN-43" data={scorecard?.grouped['MAIN-43']} />
        </Cell>
      </Row>
      <Row>
        <Cell span={8}>
          <GroupedTile metricId="MAIN-44" data={scorecard?.grouped['MAIN-44']} />
        </Cell>
        <Cell span={4}>
          <FinanceLinksTile />
        </Cell>
      </Row>
    </div>
  );
}
